import { Order } from "@/types";
import { useCallback, useMemo, useState } from "react";
import { GoogleMap, HeatmapLayer, useJsApiLoader } from '@react-google-maps/api';
import { Paper, Stack, Typography } from "@mui/material";
import neighborhoods from "@/constants/neighborhoods";
import companies from "@/constants/companies";

const locations = [...neighborhoods, ...companies];

interface Point {
    location: any;
    weight: number;
}

const libraries: ("visualization")[] = ["visualization"];

interface Props {
    customers: Record<string, NonNullable<Order['client']> & { deliveryLocation?: Order['deliveryLocation']; totalOrders?: number }>;
}

const CustomersHeadmap = ({ customers = {} }: Props) => {
    const [points, setPoints] = useState<Point[]>([]);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: import.meta.env.VITE_FIREBASE_API_KEY,
        libraries: libraries,
    });

    const customersWitGeoData = useMemo(() => {
        const placesWithOrders = Object.entries(customers).reduce<Record<string, { latitude: number; longitude: number; totalOrders: number }>>((acc, [key, customer]) => {
            if (customer?.deliveryLocation?.latitude && customer?.deliveryLocation?.longitude) {
                return {
                    ...acc,
                    [key]: {
                        latitude: customer.deliveryLocation.latitude,
                        longitude: customer.deliveryLocation.longitude,
                        totalOrders: (acc[key]?.totalOrders || 0) + (customer?.totalOrders || 1),
                    }
                };
            }

            if ((customer?.deliveryLocation as any)?.neighborhoodId) {
                const neighborhood = locations.find(n => n.slug === (customer?.deliveryLocation as any)?.neighborhoodId);

                if (neighborhood) {
                    return {
                        ...acc,
                        [key]: {
                            latitude: neighborhood.lat,
                            longitude: neighborhood.lng,
                            totalOrders: (acc[key]?.totalOrders || 0) + (customer?.totalOrders || 1),
                        }
                    };
                }
            }
            return acc;
        }, {});

        return Object.values(placesWithOrders);
    }, [customers]);

    const handleMapLoad = useCallback((map: google.maps.Map) => {
        if (window.google && customersWitGeoData?.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();

            const draft = customersWitGeoData.map(customer => {
                const lat = Number(customer?.latitude);
                const lng = Number(customer?.longitude);
                const latLng = new window.google.maps.LatLng(lat, lng);

                // 2. Extender los límites para incluir este punto
                bounds.extend(latLng);

                return {
                    location: latLng,
                    weight: customer?.totalOrders || 1,
                };
            });

            map.fitBounds(bounds);

            const listener = window.google.maps.event.addListener(map, "idle", () => {
                if (map.getZoom()! > 16) map.setZoom(16);
                window.google.maps.event.removeListener(listener);
            });

            setPoints(draft);
        }
    }, [customersWitGeoData]);

    if (!isLoaded || !customersWitGeoData.length) {
        return null;
    }

    return (
        <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
                <Typography variant="overline" color="textSecondary">Dónde se realizan los pedidos</Typography>
                <GoogleMap
                    zoom={13}
                    center={{ lat: 12.1328, lng: -86.3264 }}
                    mapContainerStyle={{ width: '100%', height: '300px' }}
                    onLoad={handleMapLoad}
                    options={{
                        styles: [
                            {
                                featureType: "all",
                                elementType: "all",
                                stylers: [
                                    { saturation: -100 }, // Removes all color
                                    { gamma: 0.5 }        // Darkens the map slightly for better contrast
                                ]
                            },
                            {
                                featureType: "water",
                                elementType: "geometry",
                                stylers: [{ color: "#c9c9c9" }] // Light gray for water
                            },
                            {
                                featureType: "road",
                                elementType: "geometry",
                                stylers: [{ visibility: "simplified" }]
                            }
                        ], // Apply the gray style here
                        disableDefaultUI: true,     // Optional: cleans up the UI
                    }}
                >
                    <HeatmapLayer
                        data={points}
                        options={{
                            radius: 30,      // Larger radius makes it look smoother
                            opacity: 0.8,    // Higher opacity for better contrast against gray
                            // Optional: Customize the gradient colors
                            gradient: [
                                'rgba(0, 255, 255, 0)',
                                'rgba(0, 255, 255, 1)',
                                'rgba(0, 191, 255, 1)',
                                'rgba(0, 127, 255, 1)',
                                'rgba(0, 63, 255, 1)',
                                'rgba(0, 0, 255, 1)',
                                'rgba(0, 0, 223, 1)',
                                'rgba(0, 0, 191, 1)',
                                'rgba(0, 0, 159, 1)',
                                'rgba(0, 0, 127, 1)',
                                'rgba(63, 0, 91, 1)',
                                'rgba(127, 0, 63, 1)',
                                'rgba(191, 0, 31, 1)',
                                'rgba(255, 0, 0, 1)'
                            ]
                        }}
                    />
                </GoogleMap>
            </Stack>
        </Paper>
    );
}

export default CustomersHeadmap;
